// #767: state the applied deposit on the quote summary, read from the
// CONFIRMED payment selection's persisted schedule snapshot. See the plan
// (docs/plans/2026-08-05-applied-deposit-from-schedule.md) for the full
// derivation, the G1 evidence gate that justifies this shape, and D1-D11 for
// every alternative considered and rejected.
//
// Home: `services/` already holds this feature's pure, transport-free
// derivation modules (`collectionTargets.ts`, `collectionPeriods.ts`,
// `evenMonthlyTerm.ts`, `planTerm.ts`); this is domain logic over a
// `PaymentSelection`, so it belongs beside them. Imports no React, no
// `api.ts`, and no component.

import type { SchedulePreview, SchedulePreviewParams } from '../types/schedule.types';
import type { DateKey } from '../utils/dateKey';
import { isRowZeroOnAnchor } from './scheduleAnchor';

/**
 * A condition that means this helper could not take a deposit figure at face
 * value: either the MEASURED elemis representation it depends on no longer
 * holds, or the confirmed selection it was handed is itself untrustworthy.
 * Never includes "this selection predates the change" — that is expected and
 * silent (see `resolveAppliedDeposit` step 4).
 *
 * The first three end in `kind: 'unstated'` (the caller renders prose and no
 * number). Only `entered-amount-disagrees` is reported ALONGSIDE a rendered
 * figure. Consumers keep a hint per code because they do not point at the
 * same investigation: the elemis response for the first two and the last, the
 * persisted selection for `entered-amount-implausible`, which also selects
 * distinct user-facing copy.
 */
export type AppliedDepositAnomaly =
    | 'anchor-mismatch' // hasDeposit request, anchor recorded, row 1 is not on it
    | 'row-zero-implausible' // row 1 is on the anchor but is not a sane deposit figure
    | 'entered-amount-implausible' // an explicit deposit is recorded but is not a sane figure
    | 'entered-amount-disagrees'; // explicit deposit sent; row 1 does not equal it

/**
 * #781 — `depositRowConfirmed` is the PUBLISHED form of the step-2 `rowOk`
 * local below (`anchorOk && isPlausibleDeposit(...)`), not a new predicate.
 * It answers exactly one question: "is `snapshot.schedule[0]` the applied
 * deposit row?" It deliberately does NOT answer "is the stated figure
 * trustworthy?" — that is what `anomaly` is for, and the two are
 * independent by design (see the `entered-amount-disagrees` case, which
 * carries `depositRowConfirmed: true` on a figure that disagrees with row
 * 1). Consumers name a ROW, never a number, off this field. `false` on
 * `kind: 'none'` is literal: no deposit was requested, so row 1 is
 * instalment 1 (contract §8 B1).
 */
export type AppliedDeposit =
    /** No deposit is taken at all. */
    | { readonly kind: 'none'; readonly depositRowConfirmed: false }
    /**
     * A definite figure, INTEGER MINOR UNITS. `source` is diagnostic only —
     * both sources render identically on the summary.
     */
    | {
          readonly kind: 'amount';
          readonly amountMinor: number;
          readonly source: 'entered' | 'schedule';
          readonly anomaly?: AppliedDepositAnomaly;
          readonly depositRowConfirmed: boolean;
      }
    /**
     * A real, non-zero deposit is taken, but its figure cannot be stated with
     * certainty. The caller MUST render prose, never a number.
     */
    | {
          readonly kind: 'unstated';
          readonly anomaly?: AppliedDepositAnomaly;
          readonly depositRowConfirmed: boolean;
      };

/**
 * The ONE plausibility rule for a figure claiming to be this quote's deposit,
 * in integer minor units. Applied to BOTH candidates — the DERIVED
 * `snapshot.schedule[0].payment` and the ENTERED `params.depositAmount` — so
 * the two branches below read as one rule rather than two.
 *
 * Extracted at the Copilot review of PR #783. Before it, only the derived
 * figure was checked; the entered one was returned on a bare
 * `typeof === 'number'`, which admits `NaN`, `Infinity`, `-5` and `3.7`. That
 * left the figure a salesperson TYPED less defended than the one artemis
 * INFERS, which is backwards — the inferred path is the one whose provenance
 * is uncontracted.
 *
 * `Number.isInteger` is what rejects `NaN` and `±Infinity` (both are
 * `typeof 'number'`); the `typeof` conjunct exists to narrow `unknown`. That
 * `unknown` is not defensive theatre: `params` reaches this module as an
 * OPAQUE, UNVALIDATED document. `StoreQuoteRequest` rules
 * `payment_selection.params` as `array` with no sub-rules — deliberately, so
 * artemis never re-derives elemis's maths — and `quotes.ts` copies `params`
 * verbatim in BOTH wire directions. So unlike `scheduleAnchorDate`, nothing
 * between the input and this line constrains `depositAmount`'s runtime shape.
 *
 * `> 0` rejects zero and negatives. `< amount` rejects a "deposit" that is the
 * whole quote — the single most dangerous wrong figure this module could print
 * (see the `<=` note on the last test case). It also fails closed for free
 * when `params.amount` is itself corrupt, since any comparison against `NaN`
 * is `false`.
 */
const isPlausibleDeposit = (value: unknown, amount: number): value is number =>
    typeof value === 'number' && Number.isInteger(value) && value > 0 && value < amount;

/**
 * The three members `resolveAppliedDeposit` actually reads. Extracted at #780
 * so the helper can serve BOTH surfaces from ONE body:
 *
 *   - the quote SUMMARY passes a CONFIRMED `PaymentSelection` (#767), which is
 *     structurally assignable to this type — that call site is unchanged;
 *   - the PAYMENT STEP passes the LIVE triple it holds before confirmation —
 *     the `params` it built (Memo B), the `snapshot` React Query returned for
 *     them, and the `anchor` Memo A read the clock for (#780).
 *
 * CALLER CONTRACT, and the one thing a new caller can get wrong: `params` and
 * `snapshot` must be a MATCHED pair — `snapshot` must be the response to
 * `params`'s own request. A live React Query surface must therefore gate on
 * `!isPlaceholderData`, because `placeholderData: keepPreviousData`
 * (`hooks/useSchedulePreview.ts:33`) deliberately serves the PREVIOUS request's
 * data while a new key is in flight. Pairing yesterday's schedule with today's
 * params is the one way this module can be made to state a wrong money figure,
 * and it is not detectable from inside a pure function. See the #780 plan's D5.
 */
export interface AppliedDepositSource {
    readonly params: SchedulePreviewParams;
    readonly snapshot: SchedulePreview;
    readonly scheduleAnchorDate?: DateKey;
}

export const isUnusableEnteredDeposit = (applied: AppliedDeposit): boolean =>
    applied.kind === 'unstated' && applied.anomaly === 'entered-amount-implausible';

/**
 * Resolves the deposit that will actually be collected, from a matched
 * params/snapshot/anchor triple. Pure: no wall clock, no React, no transport,
 * no logging — every caller owns its own observability.
 *
 * #780 widened the parameter from `PaymentSelection` to `AppliedDepositSource`.
 * The BODY did not change. A `PaymentSelection` that has passed
 * `isValidPaymentSelection` remains a valid argument, which is why
 * `QuoteSummaryCard`'s call site is untouched.
 *
 * The step order below is load-bearing and must not be rearranged — see the
 * plan's "Implementation order inside the function" note.
 */
export const resolveAppliedDeposit = (source: AppliedDepositSource): AppliedDeposit => {
    const { params, snapshot, scheduleAnchorDate } = source;

    // Step 1 — state A. `depositAmount` cannot exist without `hasDeposit`
    // (`PreviewScheduleRequest.php:37`, `prohibited_unless:hasDeposit,true`;
    // and `mapMethodToParams` only sets it inside the `hasDeposit = true`
    // branch), so no real deposit is missed by returning early here.
    if (params.hasDeposit !== true) {
        return { kind: 'none', depositRowConfirmed: false };
    }

    // Step 2 — the guard predicate, computed BEFORE the explicit branch so
    // that branch can emit its own canary (`entered-amount-disagrees`).
    //
    const row = snapshot.schedule[0];
    // #766: extracted to `scheduleAnchor.ts` when `firstCollectionDate.ts` became
    // the second consumer. `isRowZeroOnAnchor` still carries the `row !== undefined`
    // conjunct, so a true result still PROVES `row` is defined at runtime — which is
    // what makes `row.payment` below safe on an empty schedule, and why `rowOk` and
    // the explicit-deposit branch do not repeat the check. That proof is no longer
    // visible inline; do not inline this call back, and do not drop the conjunct
    // from the helper. It was never a TypeScript narrowing either way
    // (`noUncheckedIndexedAccess` is off), so this is a runtime guarantee only.
    const anchorOk = isRowZeroOnAnchor(snapshot.schedule, scheduleAnchorDate);
    const rowOk = anchorOk && isPlausibleDeposit(row.payment, params.amount);

    // Step 3 — explicit deposit. The figure is ALWAYS `params.depositAmount`,
    // never the row (D4) — a stored/entered fact is never replaced by a
    // derived one. `entered-amount-disagrees` is a live self-test of the same
    // premise the default path (steps 4-6) depends on, reported but never
    // acted on.
    //
    // The branch is entered on PRESENCE (`!== undefined`), not on
    // `typeof === 'number'`: a recorded-but-unusable figure (`null` off a JSON
    // round trip, a string off a corrupted persist, `NaN`) still means an
    // explicit deposit WAS intended and cannot be honoured. Falling through to
    // steps 4-6 would then print an INFERRED figure in place of the entered
    // one — silently, and on the closing screen. That substitution is exactly
    // what D4 forbids, so presence alone commits to this branch.
    if (params.depositAmount !== undefined) {
        // Fail closed to the prose. Never a coerced, clamped or partial figure
        // — an unusable entered deposit yields NO number, same as every other
        // failure in this module.
        //
        // `entered-amount-implausible` earns its own code rather than joining
        // `anchor-mismatch`'s "present but unverifiable" bucket. That bucket
        // is coherent because every state in it asserts an anchor IS present;
        // this one is reachable with no anchor recorded at all (test case 4's
        // shape), so reusing the code would tell the reader something untrue.
        // It also points at a different investigation — the persisted
        // selection, not the elemis response — which is what earns it a
        // distinct hint and distinct copy. `undefined` stays reserved for the
        // one state that is expected forever and must never warn (step 4).
        if (!isPlausibleDeposit(params.depositAmount, params.amount)) {
            return { kind: 'unstated', anomaly: 'entered-amount-implausible', depositRowConfirmed: rowOk };
        }
        return {
            kind: 'amount',
            amountMinor: params.depositAmount,
            source: 'entered',
            anomaly:
                rowOk && row.payment !== params.depositAmount ? 'entered-amount-disagrees' : undefined,
            depositRowConfirmed: rowOk,
        };
    }

    // Step 4 — no anchor recorded. Distinguishes "expected legacy" (silent,
    // no anomaly) from "the anchor was recorded but does not match row 1"
    // (the canary tripping).
    //
    // A recorded-but-MALFORMED anchor lands here too, and reports
    // `anchor-mismatch` rather than a fourth anomaly code: "an anchor is
    // present and row 1 could not be verified against it" is one condition
    // for the reader of the warning, and the empty-schedule case (test 7)
    // already shares it. `undefined` stays reserved for the one state that is
    // expected forever and must never warn — a pre-#767 selection.
    if (!anchorOk) {
        return {
            kind: 'unstated',
            anomaly: scheduleAnchorDate === undefined ? undefined : 'anchor-mismatch',
            depositRowConfirmed: rowOk,
        };
    }

    // Step 5 — the anchor matched, but row 1's figure is not a plausible
    // deposit (missing schedule, non-integer, zero, or >= the whole amount).
    if (!rowOk) {
        return { kind: 'unstated', anomaly: 'row-zero-implausible', depositRowConfirmed: rowOk };
    }

    // Step 6 — the default path: row 1 IS the applied deposit.
    return { kind: 'amount', amountMinor: row.payment, source: 'schedule', depositRowConfirmed: rowOk };
};
